import { FC } from 'react';
import { useAsync } from 'react-use';
import { CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import useProvisioning from 'hooks/useProvisioning';
import { AdyenWrapper, loadAdyenCCCheckout } from 'lib/adyen-client';
import { showErrorSnack } from 'modules/error';
import { accountKey, locale } from 'modules/global-wrapper';
import { isNewTabPayment, sessionInvoiceNumber } from 'modules/session-details/session-details-selectors';
import Track, { PaymentMethodEventNames } from 'modules/tracking';
import {
  postTransactionAdyenPrepare,
  postTransactionAdyenUseCcPay,
  postTransactionAdyenUseCcScaPay,
  setAdyenCCLoading,
  transactionsAdyenCCLoading,
} from 'modules/transaction-adyen';
import { CreditCard } from 'types/payment-method';
import { PrepareAdyenResp, UseAdyenCCPayResp } from 'types/transaction-adyen';
import { PAYMENT_METHODS_TYPES, SEARCH_PARAMS, THEMES, TRACKING_EVENT_STATUS } from 'utils/constants';
import st from 'utils/shared-translations';

import './adyen-cc-buy-now.css';

interface AdyenCCBuyNowProps {
  paymentDetails: CreditCard;
}

let AdyenClient: AdyenWrapper | undefined = undefined;
let card: any = undefined;
let paymentKey: string = '';
let paymentData: string = '';

const loadExternalScripts = async (prepareRespValue: PrepareAdyenResp) => {
  const { adyenJsSettings } = prepareRespValue;
  AdyenClient = await loadAdyenCCCheckout(adyenJsSettings);
};

const AdyenCCBuyNow: FC<AdyenCCBuyNowProps> = ({ paymentDetails: { paymentMethodKey } }): JSX.Element => {
  const dispatch = useAppDispatch();

  const { processQuote, processRenewal, processInvoice } = useProvisioning();

  const selectedGlobalAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);

  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);
  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const selectedAdyenCCIsLoading = useAppSelector(transactionsAdyenCCLoading);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';
  const globalTheme = localStorage.getItem(SEARCH_PARAMS.theme);

  const onSubmit = async (state: any, component: any, prepareResp: PrepareAdyenResp) => {
    const browserInfo = {
      browserUserAgent: navigator.userAgent,
      browserAcceptHeader: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
      browserLanguage: navigator.language || 'en-US',
      colorDepth: window.screen.colorDepth || 24,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timeZoneOffset: new Date().getTimezoneOffset(),
      javaEnabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,
    };

    const { paymentMethod } = prepareResp;
    try {
      if (paymentMethod) {
        const resp: UseAdyenCCPayResp = await dispatch(
          postTransactionAdyenUseCcPay({
            storedPaymentMethodId: paymentMethod.id,
            encryptedSecurityCode: state.data.paymentMethod.encryptedSecurityCode,
            origin: window.location.origin,
            deviceData: browserInfo,
            paymentMethodKey,
          }),
        ).unwrap();
        card.unmount();
        if (!resp.action && resp.isSessionInFinalState) {
          if (selectedIsNewTabPayment) window.close();
          else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.cc);
          else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.cc);
          else processQuote(PAYMENT_METHODS_TYPES.cc);
        } else if (resp.action) {
          const type = (resp.action.type || '').toLowerCase();
          if (type.includes('threeds2')) {
            const actionComponent = AdyenClient?.adyenCheckout.createFromAction(resp.action);
            actionComponent.mount('#three-ds-container');
            paymentData = resp.action.paymentData;
            paymentKey = resp.paymentKey;
          }
        }
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const onError = () => {
    if (card) card.unmount();
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
  };

  const onAdditionalDetails = async (details) => {
    dispatch(setAdyenCCLoading(true));
    const data = {
      ...details,
      details: details.data.details,
      paymentKey,
      paymentData,
    };
    try {
      const resp: UseAdyenCCPayResp = await dispatch(postTransactionAdyenUseCcScaPay(data)).unwrap();
      if (resp.isSessionInFinalState) {
        if (selectedIsNewTabPayment) window.close();
        else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.ccAdyen);
        else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.ccAdyen);
        else processQuote(PAYMENT_METHODS_TYPES.ccAdyen);
      }
    } catch {
      Track(PaymentMethodEventNames.prepareCCResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const initializeAdyenCard = async (prepareResp: PrepareAdyenResp) => {
    const configuration = {
      environment: prepareResp.adyenEnvironment,
      clientKey: prepareResp.adyenClientKey,
      countryCode: prepareResp.countryCode,

      onSubmit: (result, component) => onSubmit(result, component, prepareResp),
      onError,
      onAdditionalDetails,
    };
    try {
      if (AdyenClient) {
        let paymentMethodOption = {};
        if (prepareResp.paymentMethod) {
          paymentMethodOption = {
            storedPaymentMethodId: prepareResp.paymentMethod.id,
            brand: prepareResp.paymentMethod.brand,
            supportedShopperInteractions: prepareResp.paymentMethod.supportedShopperInteractions,
            expiryMonth: prepareResp.paymentMethod.expiryMonth,
            expiryYear: prepareResp.paymentMethod.expiryYear,
            hideCVC: false,
            hasHolderName: false,
            showPayButton: true,
          };
        }
        card = await AdyenClient.initialize(configuration, paymentMethodOption);
        dispatch(setAdyenCCLoading(false));
        card.mount('#card-container');
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  useAsync(async () => {
    if (selectedGlobalAccountKey) {
      dispatch(setAdyenCCLoading(true));
      try {
        const resp: PrepareAdyenResp = await dispatch(
          postTransactionAdyenPrepare({
            paymentMethodKey,
            browserLocale: selectedGlobalLocale,
            returnUrl: window.location.origin,
            paymentMethod: 'cca',
          }),
        ).unwrap();
        await loadExternalScripts(resp);
        initializeAdyenCard(resp);
      } catch {
        Track(PaymentMethodEventNames.prepareCCResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      }
    }
  }, [selectedGlobalAccountKey]);

  return (
    <>
      <div
        id="card-container"
        className={globalTheme?.toUpperCase() === THEMES.dark ? 'adyen-credit-card-dark-mode' : ''}
      ></div>
      <div id="three-ds-container"></div>
      {selectedAdyenCCIsLoading && (
        <div className="adyen-cc-buy-now-loader">
          <CircularProgress />
        </div>
      )}
    </>
  );
};
export default AdyenCCBuyNow;
